import type { BiasHeardFilter, BiasResultsResponse, BiasSurveyResult } from '../shared/api';
import { api, escapeHtml } from './http';
import { pageLayout } from './layout';
import { resultMapDisplayName } from './results-map-config';
import { surveyDisplayName } from './survey-display';

export async function renderBiasPage(root: HTMLElement): Promise<void> {
  root.innerHTML = pageLayout('Bias Measurements', '<div id="publicBiasResults"></div>', { publicNav: true, wide: true, showAbout: false });
  await loadBiasResults(root.querySelector<HTMLElement>('#publicBiasResults')!, 'all');
}

async function loadBiasResults(content: HTMLElement, heardFilter: BiasHeardFilter): Promise<void> {
  content.innerHTML = '<div class="loading-state"><span class="spinner"></span>Loading bias comparison…</div>';
  try {
    const parameters = new URLSearchParams();
    if (heardFilter !== 'all') parameters.set('heard', heardFilter);
    const query = parameters.size > 0 ? `?${parameters}` : '';
    const results = await api<BiasResultsResponse>(`/api/bias${query}`);
    content.innerHTML = `<section class="card bias-toolbar">
      <div>
        <h2>How does adding bias to the pro-disc golf side change support?</h2>
        <p>Only neighborhoods with responses to all three survey variants are included.</p>
        <p><strong>Alternate Survey</strong> — Replaces SavePetroPark.org with "local coalition", removes mitigations rebuttal, moves it to supporting column, shortens reasons against to only main course focused arguments. This favors the supporting arguments because they visually appear longer, Parks &amp; Rec mitigations are presented without context or fact checking, allowing their position as an authority to be presented unchallenged.</p>
        <p><strong>Shortened Survey</strong> — All adjustments from alternate survey, also removes SEPA related questions such as fern area.</p>
      </div>
      <label>Previously heard arguments
        <select id="biasHeardFilter">
          <option value="all"${results.heardFilter === 'all' ? ' selected' : ''}>All respondents</option>
          <option value="Yes"${results.heardFilter === 'Yes' ? ' selected' : ''}>Yes</option>
          <option value="No"${results.heardFilter === 'No' ? ' selected' : ''}>No</option>
        </select>
      </label>
    </section>
    ${results.neighborhoods.length === 0
      ? '<section class="card bias-empty"><h2>No qualifying neighborhoods yet</h2><p>A neighborhood will appear after all three survey variants have received at least one response from it.</p></section>'
      : `<div class="bias-groups">${results.groups.map(renderBiasGroup).join('')}</div>`}
    <p class="bias-neighborhood-footnote">*Saw bias measurement surveys. Some homes in Candlewood Ridge and Near Ridgewood were assigned to Fairwood Greens in error</p>`;
    content.querySelector<HTMLSelectElement>('#biasHeardFilter')?.addEventListener('change', (event) => {
      void loadBiasResults(content, (event.currentTarget as HTMLSelectElement).value as BiasHeardFilter);
    });
  } catch (error) {
    content.innerHTML = `<div class="error-state">${escapeHtml(error instanceof Error ? error.message : 'Bias results could not be loaded.')}</div>`;
  }
}

function renderBiasGroup(group: BiasResultsResponse['groups'][number], index: number): string {
  const displayLabel = group.neighborhood ? resultMapDisplayName(group.label) : group.label;
  return `<section class="card bias-group">
    <div class="result-header">
      <div><p class="eyebrow">${index === 0 ? 'Combined view' : 'Neighborhood'}</p><h2>${escapeHtml(displayLabel)}</h2></div>
      <span class="bias-weight-note"><small>Increasing supporting bias</small>Original → Alternate Survey → Shortened Survey</span>
    </div>
    <div class="bias-survey-columns">${group.surveys.map(renderBiasSurvey).join('')}</div>
  </section>`;
}

function renderBiasSurvey(survey: BiasSurveyResult): string {
  return `<article class="bias-survey-result bias-${survey.surveyKey}">
    <div class="bias-survey-heading">
      <span class="bias-question-label">${survey.questionLabel}</span>
      <h3>${escapeHtml(biasSurveyDisplayName(survey))}</h3>
      <p>${survey.responseCount} responses · ${survey.householdCount} households</p>
    </div>
    <div class="result-legend"><span><i class="raw-dot"></i>Raw respondents</span><span><i class="weighted-dot"></i>Household weighted</span></div>
    ${survey.options.map((option) => `<div class="result-option bias-display-option">
      <div class="result-option-label"><strong>${escapeHtml(option.option)}</strong></div>
      <div class="result-metric"><span>${formatTotal(option.weightedTotal)}</span><span>weighted</span><span>${formatPercent(option.weightedPercent)}</span></div>
      <div class="bars"><div class="bar raw-bar" style="width:${option.rawPercent * 100}%"></div><div class="bar weighted-bar" style="width:${option.weightedPercent * 100}%"></div></div>
      <div class="result-metric"><span>${option.rawCount}</span><span>raw</span><span>${formatPercent(option.rawPercent)}</span></div>
    </div>`).join('')}
  </article>`;
}

function biasSurveyDisplayName(survey: BiasSurveyResult): string {
  return surveyDisplayName(survey.surveyKey, survey.surveyLabel);
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatTotal(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
